import { Injectable, InjectionToken } from '@angular/core';
import { ObscuredData } from '@se/components';
import { Json, JsonObject } from '@se/cp.common';
import { EMPTY, Observable } from 'rxjs';
import { EcgChartState, ISeSignResultData, SeMultiSignInfo, SeSignInfo, SissData } from '../models/estensasign.model';
import { CpExamination } from '../models/custom-page';
import { Burner } from '../models/mps.models';
import { AuthData, SignResultStatus, SissUploadDocumentData } from '@se/models';

export const REPORT_WORKFLOW_EXAM_SERVICE_TOKEN = new InjectionToken('REPORT_WORKFLOW_EXAM_SERVICE_TOKEN');

export interface IReport {
    id: string;
    isAdmitted: boolean;
    isReported: boolean;
    isApproved: boolean;
    isDigitallySigned: boolean;
    reportDetailForNotification: string;
}

export interface Addendum {
    id?: string;
    idClinicalReport?: string;
    idUser: string;
    text: string;
    date?: string;
    idReferringPhysician: any;
    idSecondReferringPhysician?: string;
    isDigitallySigned: boolean;
    idDigitallySigner?: string;
    digitallySignerCode?: string;
    digitallySigningDate?: string;
    isDraft?: boolean;
    isDeleting?: boolean;
}

export interface UnlockData {
    idExamination: string;
    idReport?: string | null;
}

export interface IResponseStatus<T = IReport> {
    id: string;
    dto: T;
    responseStatusType: SignResultStatus;
    error: {
        idExamination: string;
        message: string;
        userFullName?: string;
        hResult?: number;
        innerError?: string;
        internalMessage?: string;
        exceptionName?: string;
        stackTrace?: string;
        source?: string;
        maxSignificantImagesToAdd?: number;
        significantImagesOnPacs?: number;
    };
    additionalInfo?: {
        domain: string;
        username: string;
        password: string | null;
        clientId: string;
    };
}

export interface HardCodedDataUpdate {
    idClinicalReport: string;
    hardCodedData: ObscuredData;
}

export interface ExaminationByOpenReportMode {
    id: string;
    idReport?: string | null;
    idCustomPage?: string | null;
    idCustomPageRoot?: string | null;
    idModelTypeRoots: string[];
}

export interface AddExamOptions {
    idReport: string;
    idExaminations: string[];
}

export interface ExaminationsFilter {
    name?: string;
    surname?: string;
    date?: any;
    idPatient?: string;
    idCustomPageRoot: string;
}

/*
Usare uqesta interfaccia nel metodo approve qui sotto, c'è da mettere a posto il mondo e mezzo di
metodi fatti senza usare i tipi....

export interface IApproveInfo {
    reportsIds: string[],
    authData: AuthData,
    isDeferred?: boolean,
    isFromWorklist?: boolean,
    displayedSettings?: EcgChartState,
    otherCredentialsData?: AuthData[],
    referringPhysicianId?: string
}
*/

export interface IReportWorkflowExamService {
    approve(
        reportsIds: string[],
        authData: AuthData,
        isDeferred?: boolean,
        isFromWorklist?: boolean,
        displayedSettings?: EcgChartState,
        otherCredentialsData?: AuthData[],
        referringPhysicianId?: string,
    ): Observable<IResponseStatus[]>;

    getDataForSissSign(reportId: string): Observable<SissData>;

    uploadReportToSissCloud(data: SissUploadDocumentData): Observable<ISeSignResultData[]>;

    uploadReportToSissWayCloud(data: SissUploadDocumentData): Observable<ISeSignResultData[]>;

    getPdf(info: SeSignInfo): Observable<unknown>;

    sign(info: SeSignInfo): Observable<ISeSignResultData[]>;

    multiSign(info: SeMultiSignInfo): Observable<ISeSignResultData[]>;

    undoValidation(reportId: string, authData: AuthData, isFromWorklist?: boolean): Observable<unknown>;

    undoMultiValidation(reportId: string, authData: AuthData[], isFromWorklist?: boolean): Observable<IResponseStatus>;

    forceUnlockMultiple(data: UnlockData[]): Observable<unknown>;

    unlock(data: UnlockData[]): Observable<unknown>;

    getReportExaminations(idReport: string): Observable<CpExamination[]>;

    saveAddendum(addendum: Addendum): Observable<Addendum>;

    deleteAddendum(addendum: Addendum): Observable<Addendum>;

    saveHardCodedData(data: HardCodedDataUpdate): Observable<ObscuredData>;

    burnCd?(idExaminations: string[], burner?: Burner): Observable<any>; // non è obbligatorio.....

    approveJson?(data: JsonObject): Observable<Json>;

    saveJson?(data: JsonObject): Observable<Json>;

    suspend?(idExaminations: string[], suspensionReasonId: string): Observable<any>;

    undoSuspension?(idExaminations: string[]): Observable<any>;

    retrieveAssociatedExaminations?(idExaminations: string[]): Observable<ExaminationByOpenReportMode[]>;

    searchExaminationsToAddToReport?(options: ExaminationsFilter): Observable<CpExamination[]>;

    searchExaminationsToRemoveFromReport?(idReport: string): Observable<CpExamination[]>;

    addExaminationsToReport?(options: AddExamOptions): Observable<unknown>;

    removeExaminationsFromReport?(idReport: string, idExaminations: string[]): Observable<unknown>;
}
@Injectable({
    providedIn: 'root',
})
export class NullReportWorkflowExamService implements IReportWorkflowExamService {
    approve() {
        return EMPTY;
    }

    getDataForSissSign() {
        return EMPTY;
    }

    uploadReportToSissCloud() {
        return EMPTY;
    }

    uploadReportToSissWayCloud() {
        return EMPTY;
    }

    getPdf() {
        return EMPTY;
    }

    sign() {
        return EMPTY;
    }

    multiSign() {
        return EMPTY;
    }

    undoValidation() {
        return EMPTY;
    }

    undoMultiValidation() {
        return EMPTY;
    }

    forceUnlockMultiple() {
        return EMPTY;
    }

    unlock() {
        return EMPTY;
    }

    getReportExaminations() {
        return EMPTY;
    }

    saveAddendum() {
        return EMPTY;
    }

    deleteAddendum() {
        return EMPTY;
    }

    saveHardCodedData() {
        return EMPTY;
    }

    suspend() {
        return EMPTY;
    }

    undoSuspension() {
        return EMPTY;
    }

    addExaminationsToReport() {
        return EMPTY;
    }

    searchExaminationsToAddToReport() {
        return EMPTY;
    }
}
